// React
import React, { useState, Fragment, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';

// UI
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  Tooltip,
  Typography
} from '@mui/material';
import PrimaryButton from '../ui/buttons/primaryButton';
import EditIcon from '@mui/icons-material/EditOutlined';
import TrashIcon from '@mui/icons-material/DeleteOutlined';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import FlagIcon from '@mui/icons-material/OutlinedFlag';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';

// Services
import TeamDialog from './teamDialog';
import TeamStatusDialog from './teamStatusDialog';
import SwapTeamDialog from './swapTeamDialog';
import ReservePromotionDialog from './reservePromotionDialog';
import { removeTeamFromEvent } from '../../features/team/teamSlice';

const STATUS_COLOR = {
  Active: 'success',
  Withdrawn: 'warning',
  Disqualified: 'error'
};

// Teams list + add/edit/remove/swap/status management UI for an event.
// Reused by `PlayersTeamsManager` (the standalone full-screen dialog on the
// event detail page) and by `ViewTeamsDialog` (opened via a button from the
// "Players & Teams" wizard step).
const TeamsPanel = ({ canPromoteReserve = false }) => {
  const dispatch = useDispatch();

  const [openTeam, setOpenTeam] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState();
  const [openTeamStatusDialog, setOpenTeamStatusDialog] = useState(false);
  const [openSwapTeamDialog, setOpenSwapTeamDialog] = useState(false);
  const [openReservePromotionDialog, setOpenReservePromotionDialog] = useState(false);
  const [teamForStatus, setTeamForStatus] = useState(null);
  const [teamForPromotion, setTeamForPromotion] = useState(null);
  const [removeTeamConfirmationDialogOpen, setRemoveTeamConfirmationDialogOpen] = useState(false);

  const event = useSelector((state) => state.event.event);
  const groupNameById = useMemo(() => {
    const map = new Map();
    (event.teamGroups || []).forEach((group) => {
      if (group?.id) {
        map.set(group.id, group.name || '');
      }
    });
    return map;
  }, [event.teamGroups]);

  const handleTeamAdd = () => {
    setSelectedTeam();
    setOpenTeam(true);
  };

  const handleTeamEdit = (team) => {
    setSelectedTeam(team);
    setOpenTeam(true);
  };

  const handleTeamStatus = (team) => {
    setTeamForStatus(team);
    setOpenTeamStatusDialog(true);
  };

  const handleTeamSwap = (team) => {
    setTeamForStatus(team);
    setOpenSwapTeamDialog(true);
  };

  const handleReservePromotion = (team) => {
    setTeamForPromotion(team);
    setOpenReservePromotionDialog(true);
  };

  const handleRemoveTeamConfirmationDialogOpen = (team) => {
    setSelectedTeam(team);
    setRemoveTeamConfirmationDialogOpen(true);
  };

  const handleRemoveTeamConfirmationDialogClose = () => {
    setRemoveTeamConfirmationDialogOpen(false);
  };

  const onTeamRemove = async () => {
    dispatch(removeTeamFromEvent({ eventId: event.id, teamId: selectedTeam.id }));
    handleRemoveTeamConfirmationDialogClose();
  };

  return (
    <Fragment>
      <Box display={'flex'} flexDirection={'column'}>
        <Box display={'flex'} justifyContent={'space-between'} alignItems={'center'}>
          <Typography variant="h5">Teams</Typography>
          <PrimaryButton variant={'outlined'} onClick={handleTeamAdd} color="primary">
            Add Team
          </PrimaryButton>
        </Box>

        <Box display="flex" flexDirection="column">
          {event.teams?.length > 0 ? (
            <List dense>
              {event.teams.map((team, index) => {
                const groupName = team.groupId ? groupNameById.get(team.groupId) : '';
                const status = team.status || 'Active';

                return (
                  <ListItem key={team.id} dense>
                    <Box
                      display={'flex'}
                      justifyContent={'space-between'}
                      alignItems={'center'}
                      width="100%">
                      <Box>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography>{`${index + 1}. ${team.name}`}</Typography>
                          <Chip
                            size="small"
                            label={status}
                            color={STATUS_COLOR[status] || 'default'}
                          />
                        </Box>
                        {groupName ? (
                          <Typography variant="caption">Side: {groupName}</Typography>
                        ) : null}
                        {Array.isArray(team.reserves) && team.reserves.length > 0 ? (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block">
                            Reserves:{' '}
                            {team.reserves
                              .map((reserve) =>
                                [reserve.name, reserve.surname].filter(Boolean).join(' ')
                              )
                              .join(', ')}
                          </Typography>
                        ) : null}
                        {team.statusReason ? (
                          <Typography variant="caption" color="text.secondary" display="block">
                            Reason: {team.statusReason}
                          </Typography>
                        ) : null}
                      </Box>
                      <Box flexWrap="wrap" display="flex" justifyContent="flex-end">
                        {canPromoteReserve &&
                        Array.isArray(team.reserves) &&
                        team.reserves.length > 0 ? (
                          <Tooltip title="Promote Reserve">
                            <IconButton onClick={() => handleReservePromotion(team)}>
                              <ArrowUpwardIcon color="success" />
                            </IconButton>
                          </Tooltip>
                        ) : null}
                        <Tooltip title="Swap Team (replace lineup)">
                          <IconButton onClick={() => handleTeamSwap(team)}>
                            <SwapHorizIcon color="info" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Withdraw / Disqualify / Reactivate">
                          <IconButton onClick={() => handleTeamStatus(team)}>
                            <FlagIcon color="warning" />
                          </IconButton>
                        </Tooltip>
                        <IconButton onClick={() => handleTeamEdit(team)}>
                          <EditIcon color="primary" />
                        </IconButton>
                        <IconButton onClick={() => handleRemoveTeamConfirmationDialogOpen(team)}>
                          <TrashIcon color="error" />
                        </IconButton>
                      </Box>
                    </Box>
                  </ListItem>
                );
              })}
            </List>
          ) : (
            <Typography variant="caption">No Teams Found, add a team...</Typography>
          )}
        </Box>
      </Box>

      <TeamDialog open={openTeam} setOpen={setOpenTeam} selectedTeam={selectedTeam} />

      <TeamStatusDialog
        open={openTeamStatusDialog}
        onClose={() => setOpenTeamStatusDialog(false)}
        team={teamForStatus}
        eventId={event.id}
      />

      <SwapTeamDialog
        open={openSwapTeamDialog}
        onClose={() => setOpenSwapTeamDialog(false)}
        team={teamForStatus}
      />

      <ReservePromotionDialog
        open={openReservePromotionDialog}
        onClose={() => setOpenReservePromotionDialog(false)}
        team={teamForPromotion}
      />

      <Dialog
        open={removeTeamConfirmationDialogOpen}
        onClose={handleRemoveTeamConfirmationDialogClose}
        aria-labelledby="alert-dialog-title-team"
        aria-describedby="alert-dialog-description-team">
        <DialogTitle id="alert-dialog-title-team">
          {'Your are about to delete this team'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description-team">
            Are you sure you want to delete this team? Teams that have already played a game cannot
            be removed - withdraw them instead.
          </DialogContentText>
          <DialogContentText id="alert-dialog-description-team">
            {selectedTeam?.name}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleRemoveTeamConfirmationDialogClose}>Cancel</Button>
          <Button onClick={onTeamRemove} autoFocus>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Fragment>
  );
};

export default TeamsPanel;
